import { useCacheRef } from '@doltech/core/lib/hooks/useCacheRef';
import { useElementQuerySelector } from '@doltech/core/lib/hooks/useElementQuerySelector';
import { useScrollCallback } from '@doltech/core/lib/hooks/useScrollCallback';
import { map, sortBy } from 'lodash';
import * as React from 'react';

interface UseTableOfContentScrollTrackingParams {
  sectionIds: string[];
  scrollContainerElm: string | Window | HTMLElement;
  offsetY?: number;
}

function outerHeight(el) {
  let height = el.offsetHeight;
  const style = getComputedStyle(el);

  height += parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10);
  return height;
}

export const useTableOfContentScrollTracking = (params: UseTableOfContentScrollTrackingParams) => {
  const { scrollContainerElm, sectionIds, offsetY = 0 } = params;
  const { elementRef: scrollRef } = useElementQuerySelector(scrollContainerElm);
  const { getData, hasData, setData } =
    useCacheRef<{ sectionId: string; element: HTMLElement }[]>();

  const [currentActiveSection, setCurrentActiveSection] = React.useState(sectionIds[0]);
  const retrieveDomInfo = React.useCallback(() => {
    if (!hasData()) {
      const newData = map(sectionIds, (sectionId) => {
        return {
          sectionId,
          element: document.getElementById(sectionId) as HTMLElement,
        };
      });
      setData(newData);
    }
    const sectionInfoElms = sortBy(
      map(getData(), ({ sectionId, element: domElm }) => {
        if (domElm) {
          const position = domElm.getBoundingClientRect();
          const boundaryTop = position.top;
          const boundaryBottom = position.bottom;
          return {
            id: sectionId,
            top: domElm.offsetTop,
            height: outerHeight(domElm),
            left: domElm.offsetLeft,
            width: domElm.offsetWidth,
            boundaryTop,
            boundaryBottom,
          };
        }
        return {};
      }),
      'boundaryTop'
    );

    if (sectionInfoElms && sectionInfoElms.length > 0) {
      const activeSections = sectionInfoElms.filter(
        (s) => s.top - scrollRef.current.scrollY - window.innerHeight / 2 + offsetY <= 0
      );

      const findCurrentActiveSection = activeSections[activeSections.length - 1];

      // this condition to make sure we keep previous value
      if (findCurrentActiveSection) {
        const id = findCurrentActiveSection?.id || activeSections[0]?.id;
        setCurrentActiveSection(id);
      }
    }
  }, [getData, hasData, offsetY, scrollRef, sectionIds, setData]);

  useScrollCallback(scrollRef, retrieveDomInfo);

  return {
    currentSectionId: currentActiveSection,
  };
};
